import { Category, FeatureFragment, PlanFragment } from 'src/graphql'
import { Feature, PlanFeature } from 'src/marketing/plans/types'

const mapPlanFeature = (
  featureId: string,
  features: PlanFeature[],
  plan?: PlanFragment,
) => {
  const planFeature = features.find(
    (pf: PlanFeature) =>
      pf.feature !== undefined && pf.feature.id === featureId,
  )
  const planAttr = plan ? { plan: plan.id, color: plan.color } : {}
  if (planFeature) {
    const { feature, ...rest } = planFeature
    return {
      hasFeature: true,
      ...planAttr,
      ...rest,
    }
  } else {
    return {
      hasFeature: false,
      ...planAttr,
    }
  }
}

const mapFeaturePlans = (id: string, plans: PlanFragment[]): PlanFeature[] =>
  plans.map((plan: PlanFragment) => mapPlanFeature(id, plan.features, plan))

const mapCategoryFeature = (
  id: string,
  features: Feature[],
  plans: PlanFragment[],
) => {
  const feature = features.find((feature: Feature) => feature.id === id)
  if (feature) {
    return { ...feature, plans: mapFeaturePlans(feature.id, plans) }
  } else {
    return undefined
  }
}

// Listed by category
export const pricingTableData = (
  categories: Category[],
  features: FeatureFragment[],
  plans: PlanFragment[],
): Category[] =>
  categories.map((category: Category) => ({
    ...category,
    features: category.features.map((feature: Feature) => ({
      ...feature,
      ...mapCategoryFeature(
        feature.id,
        features,
        plans.filter((p: PlanFragment) => p.features.length),
      ),
    })),
  }))

// Each plan contains all info needed about itself
const mapPlan = (
  plan: PlanFragment,
  categoryList: Category[],
  featureList: Feature[],
) => {
  const { features, ...rest } = plan
  return {
    ...rest,
    categories: categoryList.map((category: Category) => ({
      ...category,
      features: featureList
        .filter((feature: Feature) =>
          category.features.map(f => f.id).includes(feature.id),
        )
        .map((feature: Feature) => ({
          ...feature,
          ...mapPlanFeature(feature.id, plan.features),
        })),
    })),
  }
}

// Listed by plan
export const mobilePricingTableData = (
  categories: Category[],
  features: Feature[],
  plans: PlanFragment[],
) => plans.map((plan: PlanFragment) => mapPlan(plan, categories, features))

export type DecoratedPlan = ReturnType<typeof mobilePricingTableData>[0]
